import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { useI18n } from "@/i18n/I18nProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_KEYS = [
  "private_lessons",
  "ai_workshop",
  "consulting",
  "tool_feedback",
  "speaking_webinar",
  "something_else",
];

export default function ContactPage() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return t("contact.errors.name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t("contact.errors.email");
    if (!form.interest) return t("contact.errors.interest");
    if (form.message.trim().length < 5) return t("contact.errors.message");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, lang });
      setSuccess(true);
      toast.success(
        res.data?.email_sent ? t("contact.toast.sent") : t("contact.toast.received"),
      );
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch (e2) {
      console.error(e2);
      toast.error(e2?.response?.data?.detail || t("contact.errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow={t("contact.hero.eyebrow")}
        title={t("contact.hero.title")}
        description={t("contact.hero.description")}
      />

      <section data-testid="contact-section" className="bg-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <aside className="lg:col-span-4">
            <p className="eyebrow mb-4">{t("contact.info.directLine")}</p>
            <a
              href="mailto:makesense@tomsclarifies.com"
              data-testid="contact-email-link"
              className="group inline-flex items-center gap-3 text-[18px] text-brand-charcoal font-medium"
            >
              <Mail className="w-4 h-4 text-brand-blue" />
              <span className="border-b border-brand-charcoal/20 group-hover:border-brand-blue transition-colors">
                makesense@tomsclarifies.com
              </span>
            </a>

            <div className="mt-10 rounded-2xl bg-white border border-black/[0.06] p-7 soft-shadow">
              <p className="eyebrow mb-3">{t("contact.info.responseTime")}</p>
              <p className="text-[15.5px] leading-relaxed text-brand-charcoal/90">
                {t("contact.info.responseText")}
              </p>
            </div>

            <div className="mt-6 rounded-2xl bg-brand-charcoal text-brand-off p-7">
              <p className="eyebrow-light mb-3">{t("contact.info.basedIn")}</p>
              <p className="text-[16px] text-white/90">{t("contact.info.basedText")}</p>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {success ? (
              <div
                data-testid="contact-success"
                className="rounded-2xl bg-white border border-black/[0.06] p-10 soft-shadow text-center"
              >
                <CheckCircle2 className="w-10 h-10 text-brand-blue mx-auto" strokeWidth={1.5} />
                <h2 className="mt-6 text-3xl font-light tracking-tight text-brand-charcoal">
                  {t("contact.success.title")}
                </h2>
                <p className="mt-3 text-brand-muted max-w-md mx-auto">
                  {t("contact.success.description")}
                </p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  data-testid="contact-send-another"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-5 py-3 text-[13.5px] font-medium hover:bg-brand-dark transition-colors"
                >
                  {t("contact.success.another")}
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-2xl bg-white border border-black/[0.06] p-8 md:p-10 soft-shadow grid grid-cols-1 gap-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t("contact.fields.name")} required>
                    <input
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-input-name"
                      className={inputCls}
                      maxLength={200}
                    />
                  </Field>
                  <Field label={t("contact.fields.email")} required>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-input-email"
                      className={inputCls}
                    />
                  </Field>
                </div>

                <Field label={t("contact.fields.interest")} required>
                  <Select
                    value={form.interest}
                    onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-input-interest"
                      className="h-12 rounded-xl border-brand-line bg-brand-paper px-4 text-[15px] data-[placeholder]:text-brand-muted focus:ring-2 focus:ring-brand-blue/30"
                    >
                      <SelectValue placeholder={t("contact.fields.interestPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {INTEREST_KEYS.map((k) => (
                        <SelectItem key={k} value={k} data-testid={`interest-${k}`}>
                          {t(`contact.interestOptions.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label={t("contact.fields.message")} required>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    placeholder={t("contact.fields.messagePlaceholder")}
                    rows={6}
                    maxLength={5000}
                    className={`${inputCls} min-h-[140px] resize-y leading-relaxed`}
                  />
                </Field>

                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between pt-2">
                  <p className="text-[12.5px] text-brand-muted">{t("contact.fields.privacy")}</p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-6 py-3.5 text-[14px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      t("common.sending")
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" /> {t("contact.fields.submit")}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full h-12 rounded-xl border border-brand-line bg-brand-paper px-4 text-[15px] text-brand-charcoal placeholder:text-brand-muted focus:outline-none focus:border-brand-charcoal/40 focus:ring-2 focus:ring-brand-blue/20 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block mb-2 text-[12.5px] uppercase tracking-[0.16em] text-brand-muted font-medium">
        {label} {required && <span className="text-brand-blue">*</span>}
      </span>
      {children}
    </label>
  );
}
